import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

/**
 * Single Create/Edit modal for CAJE / BSJE journal entries.
 *
 * Props:
 *   mode       — "create" | "edit"
 *   kind       — "CAJE" | "BSJE" (locked once mode === "edit")
 *   initial    — { entry } when mode === "edit"; { period_id?, account_id? } optional pre-fills on create
 *   onClose()
 *   onSaved(savedEntry)  — called with the created/updated entry on success
 *
 * For Create: posts to POST /api/journal-entries
 * For Edit: posts to PATCH /api/journal-entries/{id}; only `draft` entries are editable.
 *
 * Operational scope only — no new state, no governance surfaces.
 */
export default function JournalEntryForm({ mode, kind, initial = {}, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const entry = initial.entry || null;
  const lockedKind = isEdit ? entry.entry_kind : kind;

  const [periods, setPeriods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [bsjeTemplates, setBsjeTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    period_id: entry?.period_id || initial.period_id || "",
    account_id: entry?.account_id || initial.account_id || "",
    journal_date: entry?.journal_date || new Date().toISOString().slice(0, 10),
    description: entry?.description || "",
    debit_account: entry?.debit_account || "",
    credit_account: entry?.credit_account || "",
    amount_unsigned: entry?.amount_unsigned ?? "",
    reference: entry?.reference || "",
    supporting_notes: entry?.supporting_notes || "",
    clearing_type: entry?.clearing_type || "",
  });

  // Load open periods + accounts (+ BSJE templates if relevant)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [allPeriods, allAccounts, tpls] = await Promise.all([
          api.periods({}),
          api.listAccounts(false),
          lockedKind === "BSJE" ? api.listBsjeTemplates() : Promise.resolve([]),
        ]);
        if (!mounted) return;
        // Only open periods are eligible for new entries; for edit, include the entry's own period
        const open = allPeriods.filter(p => p.status !== "locked");
        if (isEdit && entry?.period_id && !open.find(p => p.id === entry.period_id)) {
          const own = allPeriods.find(p => p.id === entry.period_id);
          if (own) open.push(own);
        }
        setPeriods(open);
        setAccounts(allAccounts);
        setBsjeTemplates(tpls || []);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || "Failed to load form data");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [lockedKind, isEdit, entry?.period_id]);

  const valid = useMemo(() => {
    return form.period_id && form.account_id && form.journal_date
      && form.description.trim() && form.debit_account.trim()
      && form.credit_account.trim()
      && Number(form.amount_unsigned) > 0;
  }, [form]);

  // ── Backwards-booking soft warning (2026-08-14) ────────────────────
  // Detect when the description keywords disagree with the DR/CR
  // direction against the selected reconciled bank account. A "Credit
  // Memo" on a bank asset should CREDIT the bank (money out); if the
  // form has the bank on the DR side we surface a soft nudge asking
  // the controller to double-check before posting. Non-blocking.
  const directionWarning = useMemo(() => {
    const acct = accounts.find(a => a.id === form.account_id);
    if (!acct) return null;
    if (acct.account_type !== "bank" && acct.account_type !== "credit_card") return null;
    const bankCode = (acct.gl_account_code || acct.account_number || "").trim();
    if (!bankCode) return null;
    const dr = form.debit_account.trim();
    const cr = form.credit_account.trim();
    // Only trigger when the bank clearly appears on exactly one side.
    const isDr = dr === bankCode;
    const isCr = cr === bankCode;
    if (!(isDr ^ isCr)) return null;
    const desc = (form.description || "").toLowerCase();
    // Keywords that imply money OUT of the bank (should be CR-side)
    const cr_keywords = ["credit memo", "withdrawal", "payment", "cheque cleared",
                          "eft out", "wire out", "service charge", "bank fee",
                          "nsf", "return", "refund issued", "outgoing"];
    // Keywords that imply money INTO the bank (should be DR-side)
    const dr_keywords = ["deposit", "credit received", "eft in", "wire in",
                          "incoming", "refund received", "interest earned"];
    const hasCrKw = cr_keywords.some(k => desc.includes(k));
    const hasDrKw = dr_keywords.some(k => desc.includes(k));
    if (hasCrKw && isDr) {
      return `Description mentions "${cr_keywords.find(k => desc.includes(k))}" which usually reduces the bank balance — but this entry DEBITS ${bankCode}. Are the debit/credit sides swapped?`;
    }
    if (hasDrKw && isCr) {
      return `Description mentions "${dr_keywords.find(k => desc.includes(k))}" which usually increases the bank balance — but this entry CREDITS ${bankCode}. Are the debit/credit sides swapped?`;
    }
    return null;
  }, [accounts, form.account_id, form.debit_account, form.credit_account, form.description]);

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  function applyTemplate(tplId) {
    const t = bsjeTemplates.find(x => x.id === tplId);
    if (!t) return;
    setForm(f => ({
      ...f,
      description: f.description || t.name || "",
      debit_account: t.debit_account || f.debit_account,
      credit_account: t.credit_account || f.credit_account,
      reference: f.reference || t.template_code || "",
    }));
  }

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      const amount = Number(form.amount_unsigned);
      if (isEdit) {
        const payload = {
          journal_date: form.journal_date,
          description: form.description.trim(),
          debit_account: form.debit_account.trim(),
          credit_account: form.credit_account.trim(),
          amount_unsigned: amount,
          reference: form.reference.trim(),
          supporting_notes: form.supporting_notes.trim(),
        };
        if (lockedKind === "CAJE") payload.clearing_type = form.clearing_type || null;
        const saved = await api.patchJournalEntry(entry.id, payload);
        onSaved && onSaved(saved);
      } else {
        const payload = {
          entry_kind: lockedKind,
          period_id: form.period_id,
          account_id: form.account_id,
          journal_date: form.journal_date,
          description: form.description.trim(),
          debit_account: form.debit_account.trim(),
          credit_account: form.credit_account.trim(),
          amount_unsigned: amount,
          reference: form.reference.trim(),
          supporting_notes: form.supporting_notes.trim(),
        };
        if (lockedKind === "CAJE" && form.clearing_type) {
          payload.clearing_type = form.clearing_type;
        }
        const saved = await api.createJournalEntry(payload);
        onSaved && onSaved(saved);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  }

  const title = isEdit
    ? `Edit ${lockedKind} ${entry?.entry_number || ""}`
    : `New ${lockedKind} Draft`;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.45)", display: "flex",
        alignItems: "center", justifyContent: "center",
      }}
      data-testid="je-form-overlay"
    >
      <div
        style={{
          background: "var(--surface, #fff)", borderRadius: 8,
          width: "640px", maxWidth: "96vw", maxHeight: "92vh",
          overflow: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
        }}
        data-testid="je-form-dialog"
      >
        <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--line)" }}>
          <div className="heading h2" data-testid="je-form-title">{title}</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            {isEdit
              ? "Only draft entries are editable. Posted entries are immutable (use Reverse instead)."
              : "Creates a draft journal entry. Post it from the detail drawer when ERP-side journal number is known."}
          </div>
        </div>

        <div style={{ padding: 22 }}>
          {loading ? (
            <div className="muted">Loading…</div>
          ) : (
            <>
              {lockedKind === "BSJE" && bsjeTemplates.length > 0 && !isEdit && (
                <Field label="From BSJE Template (optional)">
                  <select
                    onChange={(e) => applyTemplate(e.target.value)}
                    defaultValue=""
                    data-testid="je-form-bsje-template"
                  >
                    <option value="">— (none) —</option>
                    {bsjeTemplates.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.template_code} · {t.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              <Row>
                <Field label="Period" required>
                  <select
                    value={form.period_id}
                    onChange={e => set("period_id", e.target.value)}
                    disabled={isEdit}
                    data-testid="je-form-period"
                  >
                    <option value="">— select —</option>
                    {periods.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.period_label || `${p.period_year}-${String(p.period_month).padStart(2, "0")}`}
                        {p.status === "locked" ? " · locked" : ""}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Account" required>
                  <select
                    value={form.account_id}
                    onChange={e => set("account_id", e.target.value)}
                    disabled={isEdit}
                    data-testid="je-form-account"
                  >
                    <option value="">— select —</option>
                    {accounts.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </Field>
              </Row>

              <Field label="Journal Date" required>
                <input
                  type="date"
                  value={form.journal_date}
                  onChange={e => set("journal_date", e.target.value)}
                  data-testid="je-form-journal-date"
                />
              </Field>

              <Field label="Description" required>
                <input
                  type="text"
                  value={form.description}
                  onChange={e => set("description", e.target.value)}
                  placeholder="Brief description of the entry"
                  data-testid="je-form-description"
                />
              </Field>

              <Row>
                <Field label="Debit Account (DR)" required>
                  <input
                    type="text"
                    value={form.debit_account}
                    onChange={e => set("debit_account", e.target.value)}
                    placeholder="e.g. 611001-000"
                    data-testid="je-form-debit-account"
                  />
                </Field>
                <Field label="Credit Account (CR)" required>
                  <input
                    type="text"
                    value={form.credit_account}
                    onChange={e => set("credit_account", e.target.value)}
                    placeholder="e.g. 111001-000"
                    data-testid="je-form-credit-account"
                  />
                </Field>
              </Row>

              <Field label="Amount (unsigned)" required>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount_unsigned}
                  onChange={e => set("amount_unsigned", e.target.value)}
                  placeholder="0.00"
                  data-testid="je-form-amount"
                />
              </Field>

              {directionWarning && (
                <div
                  data-testid="je-form-direction-warning"
                  style={{
                    background: "#fff8e1",
                    border: "1px solid #f0c36d",
                    borderRadius: 6,
                    padding: "10px 12px",
                    fontSize: 13,
                    color: "#7a5900",
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                  }}
                >
                  <span style={{ fontWeight: 600 }}>⚠</span>
                  <span>{directionWarning} You can still post — this is a soft check for common backwards-booking errors.</span>
                </div>
              )}

              <Row>
                <Field label="Reference">
                  <input
                    type="text"
                    value={form.reference}
                    onChange={e => set("reference", e.target.value)}
                    placeholder="Optional"
                    data-testid="je-form-reference"
                  />
                </Field>
                {lockedKind === "CAJE" && (
                  <Field label="Clearing Type">
                    <select
                      value={form.clearing_type || ""}
                      onChange={e => set("clearing_type", e.target.value || "")}
                      data-testid="je-form-clearing-type"
                    >
                      <option value="">— (none) —</option>
                      <option value="merchant_clearing">merchant_clearing</option>
                      <option value="cc_clearing">cc_clearing</option>
                      <option value="other">other</option>
                    </select>
                  </Field>
                )}
              </Row>

              <Field label="Supporting Notes">
                <textarea
                  rows={3}
                  value={form.supporting_notes}
                  onChange={e => set("supporting_notes", e.target.value)}
                  placeholder="Auditor-facing notes; ERP cross-references; explanation of judgment."
                  data-testid="je-form-supporting-notes"
                />
              </Field>

              {error && (
                <div
                  data-testid="je-form-error"
                  style={{
                    marginTop: 12, padding: "10px 12px",
                    background: "var(--blocker-soft, #fff1f0)",
                    border: "1px solid var(--blocker, #d63845)",
                    borderRadius: 4, fontSize: 13, color: "var(--blocker, #d63845)",
                  }}
                >{error}</div>
              )}
            </>
          )}
        </div>

        <div style={{
          padding: "14px 22px", borderTop: "1px solid var(--line)",
          display: "flex", justifyContent: "flex-end", gap: 8,
          background: "var(--surface-elev, #fafafa)",
        }}>
          <button className="btn" onClick={onClose} disabled={submitting} data-testid="je-form-cancel">
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={submit}
            disabled={!valid || submitting || loading}
            data-testid="je-form-save"
          >
            {submitting
              ? (isEdit ? "Saving…" : "Creating…")
              : (isEdit ? "Save Changes" : "Create Draft")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{
        display: "block", fontSize: 12, fontWeight: 600,
        color: "var(--ink-mute)", marginBottom: 4,
        textTransform: "uppercase", letterSpacing: "0.04em",
      }}>
        {label}{required && <span style={{ color: "var(--blocker, #d63845)" }}> *</span>}
      </label>
      {children}
    </div>
  );
}

function Row({ children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      {children}
    </div>
  );
}
